import type {
  BackendMountOptions,
  CoreLoadOptions,
  EmulatorBackend,
  FrameCore,
  RewindStatus,
  SystemId,
} from '../types';
import { CanvasRenderer } from '../video/CanvasRenderer';
import { WebGLRenderer } from '../video/WebGLRenderer';
import type { Renderer } from '../video/Renderer';
import { AudioOutput } from '../audio/AudioOutput';
import { FrameDriver } from '../FrameDriver';
import { RewindBuffer } from '../RewindBuffer';
import { SYSTEMS } from '../systems';
import type { ShaderId } from '../video/shaders';

/**
 * シェーダを掛けるときだけ WebGL を使う。
 * WebGL が使えない環境では Canvas2D へ黙って落とす
 * （シェーダは見た目の好みなので、掛からないよりは動く方が大事）。
 */
function createRenderer(opts: BackendMountOptions): Renderer {
  const shader = opts.shader ?? 'none';
  if (shader !== 'none') {
    try {
      return new WebGLRenderer({
        integerScale: opts.integerScale ?? false,
        shader,
      });
    } catch (e) {
      console.warn('[backend] WebGL unavailable, falling back to Canvas2D', e);
    }
  }
  return new CanvasRenderer({ pixelPerfect: true, integerScale: opts.integerScale ?? false });
}

/**
 * FrameCore（こちらがフレームを回す種類のコア）を EmulatorBackend の形に包む。
 *
 * 映像・音声・タイミングはすべてこのクラスが持つので、新しいコアを追加するときは
 * FrameCore を実装するだけでよい。
 */
export class FrameCoreBackend implements EmulatorBackend {
  readonly id: string;
  readonly systems: readonly SystemId[];
  readonly displayName: string;

  private core: FrameCore;
  private renderer: Renderer | null = null;
  private audio = new AudioOutput();
  private driver: FrameDriver | null = null;
  private mountOpts: BackendMountOptions | null = null;
  private loaded = false;
  private rewindBuffer: RewindBuffer | null = null;
  private fps = 60;

  constructor(core: FrameCore, displayName: string) {
    this.core = core;
    this.id = core.id;
    this.systems = core.systems;
    this.displayName = displayName;
  }

  get running(): boolean {
    return this.driver?.running ?? false;
  }

  async mount(opts: BackendMountOptions): Promise<void> {
    this.mountOpts = opts;
    await this.core.init();

    this.renderer = createRenderer(opts);
    this.renderer.attachTo(opts.container);

    // AudioContext はユーザー操作起点で解錠する必要がある。
    // ここで失敗しても致命的ではない（無音で動く）ので握りつぶさず警告に留める。
    try {
      await this.audio.unlock();
    } catch (e) {
      console.warn('[backend] audio unlock deferred', e);
    }
    this.audio.setVolume(opts.volume);

    this.driver = new FrameDriver({
      core: this.core,
      renderer: this.renderer,
      audio: this.audio,
      pollInput: opts.pollInput,
      ...(opts.onFps ? { onFps: opts.onFps } : {}),
      ...(opts.onRewindUnavailable ? { onRewindUnavailable: opts.onRewindUnavailable } : {}),
      onError: opts.onError,
    });
  }

  async load(opts: CoreLoadOptions): Promise<void> {
    if (!this.driver || !this.renderer) throw new Error('mount() before load()');

    const spec = await this.core.load(opts);
    this.driver.setSpec(spec);
    this.fps = spec.fps;

    // 機種ごとの表示アスペクト比を適用（NES/SNES は非正方ピクセル）
    this.renderer.setDisplayAspect(SYSTEMS[opts.system].aspect);

    this.loaded = true;
  }

  start(): void {
    if (!this.loaded) throw new Error('load() before start()');
    void this.audio.unlock().catch(() => undefined);
    this.driver?.start();
  }

  pause(): void {
    this.driver?.stop();
    void this.audio.suspend();
  }

  resume(): void {
    void this.audio.resume();
    this.driver?.start();
  }

  reset(): void {
    this.core.reset();
    this.audio.flush();
    this.driver?.resetRewind();
  }

  setVolume(v: number): void {
    this.audio.setVolume(v);
    if (this.mountOpts) this.mountOpts.volume = v;
  }

  setSpeed(mult: number): void {
    this.driver?.setSpeed(mult);
  }

  setShader(id: ShaderId): boolean {
    if (!this.renderer?.setShader) return false;
    this.renderer.setShader(id);
    return true;
  }

  setCheats(codes: string[]): boolean {
    if (!this.core.setCheats) return false;
    this.core.setCheats(codes);
    return true;
  }

  async getDiscInfo(): Promise<import('../types').DiscInfo | null> {
    if (!this.core.getDiscInfo) return null;
    return this.core.getDiscInfo();
  }

  async swapDisc(index: number): Promise<boolean> {
    if (!this.core.swapDisc) return false;
    this.core.swapDisc(index);
    return true;
  }

  setRewindEnabled(on: boolean): void {
    if (!this.driver) return;
    if (on) {
      this.rewindBuffer ??= new RewindBuffer();
      this.driver.setRewindBuffer(this.rewindBuffer);
    } else {
      this.driver.setRewindBuffer(null);
      this.rewindBuffer = null;
    }
  }

  setRewinding(on: boolean): void {
    this.driver?.setRewinding(on);
  }

  get rewindStatus(): RewindStatus {
    const buffer = this.rewindBuffer;
    if (!buffer) return { available: false, active: false, seconds: 0 };
    return {
      available: buffer.enabled,
      active: this.driver?.isRewinding ?? false,
      seconds: buffer.secondsBuffered(this.fps),
      reason: buffer.disabled ?? undefined,
    };
  }

  async saveState(): Promise<Uint8Array> {
    return this.core.saveState();
  }

  async loadState(state: Uint8Array): Promise<void> {
    this.core.loadState(state);
    this.audio.flush();
    // 時系列が飛ぶので巻き戻し履歴は捨てる
    this.driver?.resetRewind();
    // 一時停止中は driver が止まっているので、1 フレーム進めて絵を更新する。
    // これがないと「読込成功」でも画面が古いままに見える。
    if (this.renderer && this.mountOpts) {
      const frame = this.core.runFrame(this.mountOpts.pollInput());
      this.core.drainAudio();
      if (frame.width > 0 && frame.height > 0) this.renderer.draw(frame);
    }
  }

  async getSram(): Promise<Uint8Array | null> {
    return this.core.getSram();
  }

  async setSram(data: Uint8Array): Promise<boolean> {
    return this.core.setSram?.(data) ?? false;
  }

  async screenshot(): Promise<Blob | null> {
    return this.renderer?.toBlob() ?? null;
  }

  /** 内部の音声出力（デバッグ HUD 用） */
  getAudioStats() {
    return this.audio.getStats();
  }

  dispose(): void {
    this.driver?.stop();
    this.audio.dispose();
    this.renderer?.dispose();
    this.core.dispose();
    this.driver = null;
    this.renderer = null;
    this.loaded = false;
  }
}
